import { useState, useRef, useEffect } from "react";
import "./DateRangeCalendar.scss";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import Button from "../Button/Button";
import { useTranslation } from "react-i18next";

export default function DateRangeCalendar({
    label = "Rango de fechas",
    required = false,
    onAccept,
    onClear,
    initialStartDate,
    initialEndDate,
}) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [hoverDate, setHoverDate] = useState(null);
    const [tempDates, setTempDates] = useState({
        start: initialStartDate,
        end: initialEndDate,
    });

    const calendarRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (
                calendarRef.current &&
                !calendarRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        setTempDates({ start: startDate, end: endDate });
    }, [startDate, endDate]);

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDay = new Date(year, month, 1).getDay();
        const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1;

        const days = [];

        for (let i = 0; i < adjustedFirstDay; i++) {
            days.push({
                date: new Date(year, month, -adjustedFirstDay + i + 1),
                isCurrentMonth: false,
            });
        }

        for (let i = 1; i <= daysInMonth; i++) {
            days.push({
                date: new Date(year, month, i),
                isCurrentMonth: true,
            });
        }

        const remainingDays = 7 - (days.length % 7);
        if (remainingDays < 7) {
            for (let i = 1; i <= remainingDays; i++) {
                days.push({
                    date: new Date(year, month + 1, i),
                    isCurrentMonth: false,
                });
            }
        }

        return days;
    };

    const isInRange = (date) => {
        if (!tempDates.start || !date) return false;
        if (!tempDates.end && hoverDate) {
            return (
                (date >= tempDates.start && date <= hoverDate) ||
                (date <= tempDates.start && date >= hoverDate)
            );
        }
        return (
            tempDates.start &&
            tempDates.end &&
            date >= tempDates.start &&
            date <= tempDates.end
        );
    };

    const isStartOrEndDate = (date) => {
        if (!date) return false;
        return (
            tempDates.start?.getTime() === date.getTime() ||
            tempDates.end?.getTime() === date.getTime()
        );
    };

    const handleDateClick = (date) => {
        if (
            !tempDates.start ||
            (tempDates.start && tempDates.end) ||
            date < tempDates.start
        ) {
            setTempDates({ start: date, end: null });
        } else {
            setTempDates((prev) => ({ ...prev, end: date }));
        }
    };

    const handleDateHover = (date) => {
        if (tempDates.start && !tempDates.end) setHoverDate(date);
    };

    const goToPreviousMonth = () => {
        setCurrentMonth(
            new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
        );
    };

    const goToNextMonth = () => {
        setCurrentMonth(
            new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
        );
    };

    const formatDate = (date) => {
        return (
            date?.toLocaleDateString("es-ES", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            }) || ""
        );
    };

    const handleAccept = () => {
        setStartDate(tempDates.start);
        setEndDate(tempDates.end);
        setIsOpen(false);
        if (onAccept) {
            onAccept({
                start: tempDates.start,
                end: tempDates.end,
            });
        }
    };

    const handleClean = () => {
        setTempDates({ start: null, end: null });
        setStartDate(null);
        setEndDate(null);
        if (onClear) {
            onClear();
        }
    };

    const getInputValue = () => {
        if (startDate && endDate) {
            return `${formatDate(startDate)} - ${formatDate(endDate)}`;
        } else if (initialStartDate && initialEndDate) {
            return `${formatDate(initialStartDate)} - ${formatDate(
                initialEndDate
            )}`;
        }
        return "";
    };

    const currentMonthName = currentMonth.toLocaleDateString("es-ES", {
        month: "long",
        year: "numeric",
    });
    const days = getDaysInMonth(currentMonth);

    const monthNames = [
        t('calendar.months.january'),
        t('calendar.months.february'),
        t('calendar.months.march'),
        t('calendar.months.april'),
        t('calendar.months.may'),
        t('calendar.months.june'),
        t('calendar.months.july'),
        t('calendar.months.august'),
        t('calendar.months.september'),
        t('calendar.months.october'),
        t('calendar.months.november'),
        t('calendar.months.december'),
    ];

    const dayNames = [
        t('calendar.days.monday'),
        t('calendar.days.tuesday'),
        t('calendar.days.wednesday'),
        t('calendar.days.thursday'),
        t('calendar.days.friday'),
        t('calendar.days.saturday'),
        t('calendar.days.sunday'),
    ];

    return (
        <div className="input date-range-picker">
            {label && (
                <label className="input__label">
                    {label}
                    {required && <span className="input__required">*</span>}
                </label>
            )}

            <div
                className="input__wrapper"
                ref={inputRef}
                onClick={() => setIsOpen(true)}
            >
                <input
                    type="text"
                    className="input__element"
                    readOnly
                    value={getInputValue()}
                    placeholder="Seleccione un rango"
                />
            </div>

            {isOpen && (
                <div className="date-range-calendar" ref={calendarRef}>
                    <div className="calendar-header">
                        <button
                            type="button"
                            onClick={goToPreviousMonth}
                            className="nav-button"
                        >
                            <IoIosArrowBack />
                        </button>
                        <h2 className="month-title">{currentMonthName}</h2>
                        <button
                            type="button"
                            onClick={goToNextMonth}
                            className="nav-button"
                        >
                            <IoIosArrowForward />
                        </button>
                    </div>

                    <div className="week-days">
                        {dayNames.map(
                            (day) => (
                                <div key={day} className="week-day">
                                    {day}
                                </div>
                            )
                        )}
                    </div>

                    <div className="calendar-grid">
                        {days.map((day, index) => {
                            const dateClasses = [
                                "calendar-day",
                                !day.isCurrentMonth && "other-month",
                                isInRange(day.date) && "in-range",
                                isStartOrEndDate(day.date) && "selected",
                            ]
                                .filter(Boolean)
                                .join(" ");

                            return (
                                <div
                                    key={index}
                                    className={dateClasses}
                                    onClick={() => handleDateClick(day.date)}
                                    onMouseEnter={() =>
                                        handleDateHover(day.date)
                                    }
                                >
                                    {day.date.getDate()}
                                </div>
                            );
                        })}
                    </div>

                    <div className="selected-dates">
                        <div className="date-info">
                            <span className="date-label">Inicio:</span>
                            <span className="date-value">
                                {formatDate(tempDates.start)}
                            </span>
                        </div>
                        <div className="date-info">
                            <span className="date-label">Fin:</span>
                            <span className="date-value">
                                {formatDate(tempDates.end)}
                            </span>
                        </div>
                    </div>

                    <div className="date-range-calendar__actions">
                        <button onClick={handleClean} className="date-range-calendar__action-btn">
                          {t('common.clean')}
                        </button>
                        <button onClick={handleAccept} className="date-range-calendar__action-btn date-range-calendar__action-btn--primary">
                          {t('common.apply')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
